import { Link } from 'react-router-dom';

import { AnchorHeading } from '../components/AnchorHeading';
import { UiPath } from '../components/UiPath';
import { useDocsPage } from '../hooks/useDocsPage';

export function SupportSetupGuide() {
  useDocsPage('Set up Support for your team — Revvly Docs');
  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/guides">Guides</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Set up Support</span>
      </nav>
      <header className="dp-header">
        <span className="rv-eyebrow-mini">Guide</span>
        <h1>
          Set up <em>Support</em> for your team.
        </h1>
        <p className="dp-oneliner">
          Everything an admin decides before customers write in: who can use Support, the roles your
          customers hold, the types of request and their statuses, how people are notified, how work
          in ClickUp is reflected back, and a checklist to go live.
        </p>
      </header>

      <section className="dp-section">
        <AnchorHeading id="enable">Turn Support on</AnchorHeading>
        <p className="dp-section-sub">
          Support is an add-on that Revvly switches on for your workspace; it is not a setting you
          flip yourself. When it is on, a Support section appears in the sidebar (Inbox, End users,
          Knowledge, Guidelines, Metrics) and a Support tab appears under Org Settings. If you
          cannot see them, ask Revvly to turn it on. The first time it is switched on, Revvly sets
          up sensible defaults: a ticket prefix made from your workspace name (AcceleratorApp
          becomes AA), three ticket types with their workflows. You can change all of them. There
          are no ready-made roles: you define your own; see below.
        </p>
        <UiPath
          steps={['Org Settings', 'Support']}
          note="Six pages: General, Ticket types & workflows, Client updates, Secret keys, Webhooks and Import. Roles are on the Knowledge page."
        />
        <p className="dp-section-sub">
          Every active member of the workspace can work tickets. Only admins and owners can change
          these settings; other members see the pages read-only with a notice. Add people with{' '}
          <Link to="/docs/guides/add-user">Add a team member</Link>.
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="general">General settings</AnchorHeading>
        <UiPath steps={['Org Settings', 'Support', 'General']} note="Admins and owners." />
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">Ticket prefix</div>
            <div className="dp-field-v">
              2 to 8 letters or digits, shown before every ticket number. It applies to new tickets
              only.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Auto-close</div>
            <div className="dp-field-v">
              Closes tickets that have waited on the customer for the hours you set (1 to 720, 48 by
              default). See{' '}
              <Link to="/docs/guides/support-inbox#close">Work the support inbox</Link>.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">AI answers first</div>
            <div className="dp-field-v">
              The AI replies first on request types that allow it. See{' '}
              <Link to="/docs/guides/support-ai#switches">How the AI first line works</Link>.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Limit daily AI spend</div>
            <div className="dp-field-v">
              A cap in US dollars per UTC day. When reached, new tickets go straight to agents.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Email clients who are away</div>
            <div className="dp-field-v">
              A public reply the customer has not read after this many minutes (1 to 1,440, 5 by
              default) is emailed to them. They can turn these emails off. Optionally add the page
              where customers read replies (an https address) so the email links to it; without one
              the email asks them to reply in your app.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Widget allowed origins</div>
            <div className="dp-field-v">
              The sites your chat runs on, such as{' '}
              <span className="api-inline">https://app.example.com</span>. https only, no paths, http
              allowed for localhost, up to 100. Write <span className="api-inline">https://*.example.com</span>{' '}
              to cover every subdomain (not <span className="api-inline">example.com</span> itself:
              list it on its own line, as you do each white-label domain). With none, the widget
              loads nowhere.
            </div>
          </div>
        </div>
      </section>

      <section className="dp-section">
        <AnchorHeading id="roles">Roles</AnchorHeading>
        <UiPath
          steps={['Support', 'Knowledge', 'Roles', 'New role']}
          note="Admins and owners. The Roles button is at the top of the Knowledge page."
        />
        <p className="dp-section-sub">
          A role is a kind of customer who should see different help articles, such as Founder or
          Mentor. Revvly has no built-in roles: you define every one. You do not change what your
          system sends to fit Revvly. Instead each role says which values it matches in what your
          system already sends about a person: their role value, or one of their attributes. A
          person can have several roles, and reads articles open to any of them plus everything open
          to everyone. A person who matches none reads only what is open to everyone.
        </p>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">Name, Description</div>
            <div className="dp-field-v">
              How the role is listed here, on articles and in filters.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Who has this role</div>
            <div className="dp-field-v">
              One or more rules. <strong>Role value is any of</strong> compares the{' '}
              <span className="api-inline">role</span> your system sends;{' '}
              <strong>Attribute [key] is any of</strong> compares one of the person&rsquo;s
              attributes, for example <span className="api-inline">programme_role</span> is{' '}
              <span className="api-inline">mentor</span> or{' '}
              <span className="api-inline">coach</span>. Any one rule is enough. Values match
              whatever their case and surrounding spaces. Name, email and plan are not matched. A
              role with no rules matches nobody. While you write an attribute rule, the editor lists
              the attribute keys your people already carry (from what your system sends and from an
              Intercom import), with how many people hold each; once you pick a key it lists that
              key&rsquo;s values, most common first, with how many people hold each. Click one to
              fill the rule in.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Help auto-guessing</div>
            <div className="dp-field-v">
              Optional paths, section names and keywords that mark a role&rsquo;s articles in your
              help site. Knowledge uses them to guess who can read crawled and Intercom articles.
              They do not decide who has the role.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Retired</div>
            <div className="dp-field-v">A retired role grants nobody anything.</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Internal key</div>
            <div className="dp-field-v">
              Made from the name when you create the role and never changed, so renaming a role or
              editing its values never breaks the articles that name it. Shown on the role for when
              you push articles through the API. It is not something your tokens have to send.
            </div>
          </div>
        </div>
        <p className="dp-section-sub">
          Changes to a role apply to the next answer: there is no need to sign anyone in again.
          Deleting a role takes it off every article. An article that only that role could read is
          never opened to everyone: it waits under Needs a decision, out of answers, until you
          choose who can read it. The delete confirmation tells you how many articles name the role.
        </p>
        <p className="dp-section-sub">
          People appear under Support, End users once they have signed in to the chat or been synced
          by your system, with the roles they match.
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="types">Ticket types and workflows</AnchorHeading>
        <UiPath
          steps={['Org Settings', 'Support', 'Ticket types & workflows']}
          note="Admins and owners. Add type and New workflow are at the top right."
        />
        <p className="dp-section-sub">
          A <strong>type</strong> is a kind of request that the customer picks. Each type uses a{' '}
          <strong>workflow</strong>: an ordered list of statuses. Three types are set up for you:
          Question (New, Answered, Closed), Issue report (New, Replicated, Escalated, In
          development, Resolved, Closed) and Suggestion (Received, Under review, Accepted, In
          roadmap, Delivered, Declined, Closed). Several types can share a workflow.
        </p>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">Category</div>
            <div className="dp-field-v">
              Every status belongs to Open, In progress, Pending or Closed. The category, not the
              name, drives the views and the rules: Pending is Waiting on client and what auto-close
              watches; Closed is closed.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Initial and closed status</div>
            <div className="dp-field-v">
              Each type names the status new tickets start in, and the one a close lands on.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">AI answers this type first</div>
            <div className="dp-field-v">Per-type switch for the AI.</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Limit allowed transitions</div>
            <div className="dp-field-v">
              Off means any status can move to any other. On, only the listed moves are allowed.
            </div>
          </div>
        </div>
        <p className="dp-section-sub">
          Existing tickets stay valid when you edit. A saved status keeps its category and is
          retired instead of removed. Every workflow keeps at least one open status and one closed
          status, and at least one type stays live. A type&rsquo;s workflow is fixed once it has
          tickets; to use another, create a new type. Retiring a type stops new requests of that
          kind.
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="teams">Teams and assignment</AnchorHeading>
        <UiPath
          steps={['Org Settings', 'Teams']}
          note="Teams are shared with the rest of Revvly."
        />
        <p className="dp-section-sub">
          Create a team there, then on a ticket choose it in the Team chip, and in the inbox filter
          by it. A ticket&rsquo;s team and assignee are independent: a team can hold tickets nobody
          has taken yet. Assignment rules (sending tickets to a team or person automatically) are
          not built; tickets are taken and assigned by people.
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="notifications">Notifications</AnchorHeading>
        <p className="dp-section-sub">
          <strong>For agents.</strong> Each agent sets their own, from the bell and its Notification
          preferences link (Support, Notifications). Each kind can reach them in the app, by email,
          or both:
        </p>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">Ticket created</div>
            <div className="dp-field-v">Every agent. Default: in the app.</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Assigned to you</div>
            <div className="dp-field-v">Default: app and email.</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Reply on your ticket</div>
            <div className="dp-field-v">
              The customer writes on your ticket. Default: app and email.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Status changed on your ticket</div>
            <div className="dp-field-v">Default: in the app.</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Mentioned</div>
            <div className="dp-field-v">Named in an internal note. Default: app and email.</div>
          </div>
        </div>
        <p className="dp-section-sub">
          The email carries the ticket number, title and a link, never the message text, and is sent
          promptly, and not at all if it is more than a day old by the time it could go. Nobody is
          notified of their own action.
        </p>
        <p className="dp-section-sub">
          <strong>For customers.</strong> Two choices belong to the customer, not to you, and your
          system cannot switch them back on for them:
        </p>
        <ul className="dp-connects">
          <li>
            <strong>Email about an unread reply</strong>: on by default. You set how long to wait
            under General. Every email has a Turn off these emails link, and your chat can offer the
            same choice.
          </li>
          <li>
            <strong>Progress updates</strong> on the work behind a request: All (every stage that
            notifies), Final (only the last stage) or None. It controls the messages described under{' '}
            <a href="#client-updates">client updates</a>.
          </li>
        </ul>
        <p className="dp-section-sub">
          Offering them to customers is part of your chat; see{' '}
          <Link to="/docs/api/support-chat#notifications">
            Support chat API, notification preferences
          </Link>
          .
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="client-updates">Client updates</AnchorHeading>
        <UiPath
          steps={['Org Settings', 'Support', 'Client updates']}
          note="Admins and owners. Only useful once you link ClickUp tasks to tickets."
        />
        <p className="dp-section-sub">
          When a ticket is backed by work in ClickUp, the customer never sees ClickUp&rsquo;s
          statuses. They see <strong>stages</strong> that you name: Received, Confirmed, In
          development, In testing and Released to begin with. You can rename, reorder, add and
          remove them. For each stage:
        </p>
        <ul className="dp-connects">
          <li>
            <strong>Tell the client when their request reaches this stage</strong> sends them a
            message. You write it, using only <span className="api-inline">{'{{title}}'}</span>,{' '}
            <span className="api-inline">{'{{stage}}'}</span> and{' '}
            <span className="api-inline">{'{{number}}'}</span>, up to 500 characters; a preview
            shows what they read.
          </li>
          <li>
            <strong>Also tell them if the work moves back into this stage</strong>: by default only
            forward moves are announced.
          </li>
          <li>
            <strong>When work reaches this stage, move the ticket to</strong> a status. A closed
            status cannot be chosen, so a close always carries a resolution an agent chose.
          </li>
        </ul>
        <p className="dp-section-sub">
          Each stage is announced at most once per request, only when the work moves forward, and
          after a ten minute wait so that several quick moves make one message about the latest
          stage. Two more options are at the bottom: tell a customer who has already closed their
          request, and add a comment to the linked ClickUp task when a ticket changes status, closes
          or reopens. Below, <strong>ClickUp statuses</strong> maps each status of a ClickUp list to
          a stage; statuses you leave unmapped stay internal.
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="clickup">Link ClickUp tasks</AnchorHeading>
        <UiPath
          steps={['Org Settings', 'Integrations', 'ClickUp']}
          note="An admin connects ClickUp once for the workspace. Revvly needs write access to ClickUp."
        />
        <p className="dp-section-sub">
          On a ticket, the ClickUp panel can <strong>Create ClickUp task</strong> (choose the list,
          or let the default list for that type apply) or take a pasted ClickUp task link. Up to ten
          tasks can be linked to one ticket, and one ticket can only have one task created from it.
          A task Revvly creates carries the ticket number and link, its type and priority, the
          client, who asked (name, role and plan) and their first public message. It never carries
          an internal note or an email address.
        </p>
        <p className="dp-section-sub">
          Revvly only ever adds to ClickUp: it creates tasks, and writes a comment on the tasks it
          linked. It does not move or edit an existing task. Status changes in ClickUp usually reach
          the ticket within moments, and a check every 15 minutes repairs anything missed. The
          default list for each type is set under Org Settings, Support, Client updates, Where tasks
          start.
        </p>
        <div className="api-note">
          <span className="api-note-icon">i</span>
          <span>
            Not built yet: rules that create ClickUp tasks automatically, and AI-written stage
            updates. Tasks are created or linked by an agent.
          </span>
        </div>
      </section>

      <section className="dp-section">
        <AnchorHeading id="webhooks">Webhooks</AnchorHeading>
        <UiPath
          steps={['Org Settings', 'Support', 'Webhooks', 'Add endpoint']}
          note="Admins and owners. Up to five endpoints."
        />
        <p className="dp-section-sub">
          Revvly posts the ticket events you choose to your backend, signed, so it can pass them on
          to your users or react to them. Enter an https address and tick the events you want. Each
          endpoint has its own signing secret, shown once when you create it; Rotate secret replaces
          it. Send test event checks that your backend answers. If an endpoint fails ten deliveries
          in a row it is turned off until you turn it on again, and each delivery is retried up to
          six times first. Delivery history is kept for 30 days and a finished delivery can be
          replayed. The body and signature are described in{' '}
          <Link to="/docs/api/support-chat#webhook">the Support chat API reference</Link>.
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="keys">Secret keys</AnchorHeading>
        <UiPath
          steps={['Org Settings', 'Support', 'Secret keys', 'Create secret key']}
          note="Admins and owners. The key is shown once."
        />
        <p className="dp-section-sub">
          Your backend uses a secret key to sign customers in to the chat. It stays on your server.
          Limits sets how many requests a key may make per second and per minute. The steps for
          using it are in{' '}
          <Link to="/docs/guides/support-tokens">Sign users in to the support chat</Link>.
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="import-intercom">Import your Intercom conversations</AnchorHeading>
        <UiPath steps={['Org Settings', 'Support', 'Import']} note="Admins and owners." />
        <p className="dp-section-sub">
          Copies up to 24 months of your Intercom conversations into Support as tickets, with their
          original dates, so your team keeps the history and you can compare Revvly with Fin on them
          (Support, Compare with Intercom). Revvly only reads from Intercom and tells nobody about
          imported history. This is not the help-centre import on the Knowledge page, which brings
          in articles, not conversations.
        </p>
        <p className="dp-section-sub">
          <strong>1. Get an Intercom access token.</strong> You need to be an admin in Intercom.
        </p>
        <ol className="dp-connects">
          <li>
            In Intercom, open Settings, then Integrations, then <strong>Developer Hub</strong>.
          </li>
          <li>
            Select <strong>New app</strong>, name it something like &ldquo;Revvly import&rdquo;,
            choose the workspace you want to import from, and create it.
          </li>
          <li>
            Open <strong>Authentication</strong>. Make sure the app can read conversations, read
            users and companies, and read admins, and save.
          </li>
          <li>
            Copy the <strong>Access token</strong>. Treat it like a password: it reads your whole
            inbox.
          </li>
        </ol>
        <p className="dp-section-sub">
          <strong>2. Start the import.</strong>
        </p>
        <ol className="dp-connects">
          <li>Paste the token into Intercom access token.</li>
          <li>
            Choose where your workspace is hosted: Europe if your Intercom address starts with
            app.eu.intercom.com, Australia for app.au.intercom.com, otherwise United States.
          </li>
          <li>
            Choose how far back to import (at most 24 months), then select{' '}
            <strong>Start import</strong>.
          </li>
        </ol>
        <p className="dp-section-sub">
          The import runs in the background and survives restarts; the page shows its progress.
          Revvly encrypts the token, uses it for this import only and discards it when the import
          ends, so you can delete the app in Intercom afterwards. When it finishes, compare the
          number of conversations it saw with Intercom&rsquo;s own count for the same period, and
          open the failures list if there is one: each line is a conversation and the reason it
          could not be copied. Running the import again is safe: conversations already imported are
          skipped and only new messages are added. Run it once more if your conversations were
          imported before 15 October 2026, so Fin&rsquo;s outcome is kept for the comparison with
          Intercom.
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="checklist">Go-live checklist</AnchorHeading>
        <ol className="dp-connects">
          <li>Support is on, and everyone who should work tickets is a member of the workspace.</li>
          <li>
            Every kind of customer who should see different articles has a role whose rules match
            the role value or attributes your system sends, and plans are spelled the way your
            system sends them.
          </li>
          <li>
            Ticket types and statuses match how you work, including which statuses are Pending.
          </li>
          <li>
            Knowledge sources are added, read and healthy, nothing is waiting under Needs a
            decision, and the Knowledge badge in the sidebar is clear. See{' '}
            <Link to="/docs/guides/support-knowledge">Build the knowledge base</Link>.
          </li>
          <li>
            A secret key exists and your backend creates tokens only for the signed-in user. See{' '}
            <Link to="/docs/guides/support-tokens">Sign users in to the support chat</Link>.
          </li>
          <li>
            The widget is on your site and every production address is in Widget allowed origins.
            See <Link to="/docs/guides/support-widget">Add a support chat to your app</Link>.
          </li>
          <li>
            Guidelines tell the AI the tone for each role, the context your articles leave out, and
            which questions to leave to your team. See{' '}
            <Link to="/docs/guides/support-guidelines">Write guidelines for the support AI</Link>.
          </li>
          <li>
            You decided on auto-close, the AI switch and a daily spending cap, and set the AI switch
            on the types where you want it.
          </li>
          <li>
            Agents checked their notification preferences, and clients can turn off emails about
            unread replies.
          </li>
          <li>
            You tried each role in the sandbox and read the answers as that customer. See{' '}
            <Link to="/docs/guides/support-ai#sandbox">the sandbox</Link>.
          </li>
        </ol>
      </section>

      <section className="dp-section">
        <h2>Related</h2>
        <ul className="dp-connects">
          <li>
            <Link to="/docs/guides/support-inbox">Work the support inbox ›</Link>
          </li>
          <li>
            <Link to="/docs/guides/support-knowledge">
              Build the knowledge base the AI answers from ›
            </Link>
          </li>
          <li>
            <Link to="/docs/guides/support-ai">How the AI first line works ›</Link>
          </li>
          <li>
            <Link to="/docs/api/support-chat">Support chat API reference ›</Link>
          </li>
        </ul>
      </section>
    </div>
  );
}
